import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import SequenceBarItem from 'Components/UI/SequenceBarItem';
import MuiCircle from '@material-ui/icons/FiberManualRecord';
import moment from 'moment';
import { dayOfMonthNoYear } from 'Consts/DateFormats';
import { fade } from '@material-ui/core';
import { ordinalizeNumber } from 'Helpers/Utils';
import MuiIconButton from '@material-ui/core/IconButton';
import { openModal } from 'Helpers/ModalHelper';
import modalConsts from 'Consts/ModalNames';
import MuiAddCircleIcon from '@material-ui/icons/AddCircle';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import useUserContext from 'Context/UserContext';
import fr from 'facet-rest';
import { pickImplementation } from 'Helpers/Affordances';
import history from 'State/history';
import { advisorHouseholdSchedulerUri } from 'Consts/advisorUris';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';

const styles = theme => ({
  root: {
    width: '100%',
    alignItems: 'center',
  },
  footerText: {
    ...theme.typography.h6,
    fontWeight: theme.typography.fontWeightBold,
    color: fade(theme.palette.facet.primaryGray, 0.8),
    marginBottom: 'unset',
  },
  nextProjectedColor: {
    color: theme.palette.secondary.main,
  },
  uncategorizedMeetingColor: {
    color: theme.palette.warning.main,
  },
  headerText: {
    color: fade(theme.palette.facet.primaryGray, 0.8),
    fontWeight: theme.typography.fontWeightBold,
  },
  icon: {
    color: fade(theme.palette.facet.primaryGray, 0.8),
  },
});

const ProjectedMeetingSequenceBarEvent = ({
  classes,
  className,
  resource,
  count,
  areMeetingsCategorized,
}) => {
  const { householdResource } = useSimpleHousehold();
  const { agentResource } = useUserContext();
  const { logEvent } = useEventTrackingLogEvent();
  const nextProjectedMeeting = count === 1;
  const handleNextProjectedMeetingClick = pickImplementation(agentResource, {
    [fr.relationships.default.name]: () => {
      logEvent('Clicked Next Projected Meeting');
      if (areMeetingsCategorized) {
        history.push(advisorHouseholdSchedulerUri(householdResource.id(), {
          link: resource.relationship(fr.relationships.projectedMeetingAvailabilityPeriod.name).link('related').url(),
          projectedMeetingLink: resource.link('self').url(),
        }));
      } else {
        openModal(modalConsts.uncategorizedMeetingModal);
      }
    },
  });
  return (
    <SequenceBarItem
      className={classNames(classes.root, className)}
      footer={
        <>
          <div className={classNames(classes.footerText, {
            [classes.nextProjectedColor]: nextProjectedMeeting,
            [classes.uncategorizedMeetingColor]: nextProjectedMeeting && !areMeetingsCategorized,
          })}
          >
            {nextProjectedMeeting ? 'Next' : ordinalizeNumber(count)} Projected
          </div>
          <div
            test="schedule-next-item-date"
            className={classNames(classes.footerText, {
              [classes.nextProjectedColor]: nextProjectedMeeting,
              [classes.uncategorizedMeetingColor]: nextProjectedMeeting && !areMeetingsCategorized,
            })}
          >
            Week of {moment(resource.attr('start')).format(dayOfMonthNoYear)}
          </div>
        </>
      }
    >
      {nextProjectedMeeting ? (
        <MuiIconButton
          className={classNames({
            [classes.nextProjectedColor]: nextProjectedMeeting,
            [classes.uncategorizedMeetingColor]: !areMeetingsCategorized,
          })}
          test="schedule-next-item-button"
          onClick={handleNextProjectedMeetingClick}
        >
          <MuiAddCircleIcon />
        </MuiIconButton>
      ) : (
        <MuiCircle className={classes.icon} />
      )}
    </SequenceBarItem>
  );
};

ProjectedMeetingSequenceBarEvent.propTypes = {
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
  resource: PropTypes.object.isRequired,
  /**
   * Specifies which nth projected item this is.
   */
  count: PropTypes.number.isRequired,
  areMeetingsCategorized: PropTypes.bool.isRequired,
};

ProjectedMeetingSequenceBarEvent.defaultProps = {
  className: '',
};

export default withStyles(styles)(ProjectedMeetingSequenceBarEvent);
